/**
 * Phase 5F — Production Planner (light-theme legible variant)
 *
 * Three tabs over the v2 cards layer:
 *   - Production: kanban-style by stage (calls /v2/workspaces/{ws}/cards)
 *   - Week:       Mon–Sun bucketed scheduled / due cards (/v2/workspaces/{ws}/week)
 *   - Calendar:   month-view (/v2/workspaces/{ws}/calendar)
 */
import React, { useEffect, useState, useCallback, useMemo } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Badge } from "../components/ui/badge";
import { Skeleton } from "../components/ui/skeleton";
import { Button } from "../components/ui/button";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "../components/ui/tooltip";
import { ChevronLeft, ChevronRight, AlertTriangle, Kanban, CalendarDays, CalendarRange, History, Sparkles, X, Layers, FileText, Search, ArrowDownUp, Clock, ClipboardList, Upload } from "lucide-react";
import { toast } from "sonner";
import V2WizardModal from "../components/V2WizardModal";
import AdHocTaskModal from "../components/AdHocTaskModal";
import CoverStudioModal from "../components/CoverStudioModal";
import CoverLibraryDrawer, { OpenCoverLibraryButton } from "../components/CoverLibraryDrawer";
import V2CardDrawer from "../components/V2CardDrawer";
import V2TemplateDrawer from "../components/V2TemplateDrawer";
import CompletedHistoryPanel from "../components/CompletedHistoryPanel";
import CardPriorityBadge, { PRIORITY_META } from "../components/CardPriorityBadge";
import { useNavigate, useLocation } from "react-router-dom";

// Same-origin in browser so the session cookie (scoped to the page's host) is sent on every API call.
// Falls back to the build-time env var only for SSR / localhost.
const _API_BASE = (typeof window !== 'undefined' && window.location.hostname !== 'localhost') ? window.location.origin : (process.env.REACT_APP_BACKEND_URL || (typeof window !== 'undefined' ? window.location.origin : ''));
const API = `${_API_BASE}/api`;

// REX-005 (Feb 2026): Design Gateway simplification.
// IN_REVIEW lane is removed from the planner UI. Designer goes
// IN_DESIGN → PENDING_APPROVAL ("Awaiting Approval") directly.
// IN_REVIEW remains in the colour/label maps below in case any
// legacy cards still hold that stage — they'll render correctly
// in lookups (e.g. drawer breadcrumbs) but won't get a swim lane.
const STAGE_ORDER = [
  "BACKLOG", "READY_FOR_DESIGN", "IN_DESIGN",
  "PENDING_APPROVAL", "APPROVED", "REJECTED", "SCHEDULED",
  "PUBLISHED", "COMPLETED",
];

// Plain-English meaning for each stage — used in column header tooltips so
// new users don't have to guess what BACKLOG vs IN_REVIEW etc. actually mean.
const STAGE_DESCRIPTIONS = {
  BACKLOG:           "Drafts that exist but haven't been pushed forward yet — your raw idea pile.",
  READY_FOR_DESIGN:  "Brief is locked. Designer can pick this up.",
  IN_DESIGN:         "Designer is actively working on the visuals/layout.",
  IN_REVIEW:         "Draft + visuals done — internal review pass.",
  PENDING_APPROVAL:  "Awaiting final sign-off before scheduling. Anyone can move a card here; only approvers can move it forward.",
  APPROVED:          "Cleared. Just needs a slot in the schedule. Approver-only column.",
  REJECTED:          "Sent back — needs rework or won't ship. Approver-only column.",
  SCHEDULED:         "Queued in Buffer / LinkedIn — will auto-publish on its date. Approver-only column.",
  PUBLISHED:         "Live on the platform. Approver-only column.",
  COMPLETED:         "Live + final metrics logged. Closed. Approver-only column.",
};

// Voice badge styling — small inline pill on every card chip.
const VOICE_BADGE = {
  remi_personal:           { label: "Remi",        cls: "bg-rose-200 text-rose-900 border-rose-300" },
  resiligent_institutional:{ label: "RESILIGENT",  cls: "bg-slate-200 text-slate-900 border-slate-400" },
  employee_voice:          { label: "Employee",    cls: "bg-amber-200 text-amber-900 border-amber-300" },
};

// Solid pastel chip colors with strong dark text — high contrast on white
const STAGE_COLORS = {
  BACKLOG:           "bg-slate-100   text-slate-800   border-slate-300",
  READY_FOR_DESIGN:  "bg-sky-100     text-sky-900     border-sky-300",
  IN_DESIGN:         "bg-indigo-100  text-indigo-900  border-indigo-300",
  IN_REVIEW:         "bg-violet-100  text-violet-900  border-violet-300",
  PENDING_APPROVAL:  "bg-amber-100   text-amber-900   border-amber-300",
  APPROVED:          "bg-emerald-100 text-emerald-900 border-emerald-300",
  REJECTED:          "bg-red-100     text-red-900     border-red-300",
  SCHEDULED:         "bg-cyan-100    text-cyan-900    border-cyan-300",
  PUBLISHED:         "bg-fuchsia-100 text-fuchsia-900 border-fuchsia-300",
  COMPLETED:         "bg-slate-200   text-slate-800   border-slate-400",
};

// User-facing labels — internal stage keys stay as-is so backend / API
// contracts don't have to change. May 2026: "Backlog" → "Idea Stage",
// "Pending Approval" → "Awaiting Approval" per user request.
const STAGE_LABELS = {
  BACKLOG:           "Idea Stage",
  READY_FOR_DESIGN:  "Ready for Design",
  IN_DESIGN:         "In Design",
  IN_REVIEW:         "In Review",
  PENDING_APPROVAL:  "Awaiting Approval",
  APPROVED:          "Approved",
  REJECTED:          "Rejected",
  SCHEDULED:         "Scheduled",
  PUBLISHED:         "Published",
  COMPLETED:         "Completed",
};

// Pre-approval stages — anyone signed in can drag here. Gated stages are
// approver-only (server-enforced + UI shows a friendly toast).
const PRE_APPROVAL_STAGES = new Set([
  "BACKLOG", "READY_FOR_DESIGN", "IN_DESIGN", "IN_REVIEW", "PENDING_APPROVAL",
]);
const GATED_STAGES = new Set([
  "APPROVED", "REJECTED", "SCHEDULED", "PUBLISHED", "COMPLETED",
]);

// Swim-lane body tints — keep the column alive end-to-end (May 2026).
const STAGE_LANE_BG = {
  BACKLOG:           "bg-slate-50",
  READY_FOR_DESIGN:  "bg-sky-50",
  IN_DESIGN:         "bg-indigo-50",
  IN_REVIEW:         "bg-violet-50",
  PENDING_APPROVAL:  "bg-amber-50",
  APPROVED:          "bg-emerald-50",
  REJECTED:          "bg-red-50",
  SCHEDULED:         "bg-cyan-50",
  PUBLISHED:         "bg-fuchsia-50",
  COMPLETED:         "bg-stone-100",
};
const STAGE_HEADER_BG = {
  BACKLOG:           "bg-slate-200   text-slate-900   border-b-slate-300",
  READY_FOR_DESIGN:  "bg-sky-200     text-sky-900     border-b-sky-300",
  IN_DESIGN:         "bg-indigo-200  text-indigo-900  border-b-indigo-300",
  IN_REVIEW:         "bg-violet-200  text-violet-900  border-b-violet-300",
  PENDING_APPROVAL:  "bg-amber-200   text-amber-900   border-b-amber-300",
  APPROVED:          "bg-emerald-200 text-emerald-900 border-b-emerald-300",
  REJECTED:          "bg-red-200     text-red-900     border-b-red-300",
  SCHEDULED:         "bg-cyan-200    text-cyan-900    border-b-cyan-300",
  PUBLISHED:         "bg-fuchsia-200 text-fuchsia-900 border-b-fuchsia-300",
  COMPLETED:         "bg-stone-200   text-stone-900   border-b-stone-300",
};

async function apiCall(path) {
  return fetch(`${API}${path}`, { credentials: "include" });
}

function fmtDate(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  return d.toLocaleDateString("en-GB", { weekday: "short", day: "2-digit", month: "short" });
}

// "Time in current stage" — how long a card has been sitting in its stage.
// Drives the obvious age badge on every card chip so a stuck card is visible
// at a glance. Colour escalates: <2d green, 2–5d amber, >5d red.
function timeInStage(iso) {
  if (!iso) return null;
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return null;
  const mins = Math.max(0, Math.floor((Date.now() - then) / 60000));
  const days = Math.floor(mins / 1440);
  const hours = Math.floor((mins % 1440) / 60);
  let label;
  if (days >= 1) label = `${days}d`;
  else if (hours >= 1) label = `${hours}h`;
  else label = `${Math.max(1, mins)}m`;
  let cls;
  if (days >= 5) cls = "bg-red-100 text-red-800 border-red-300";
  else if (days >= 2) cls = "bg-amber-100 text-amber-800 border-amber-300";
  else cls = "bg-emerald-100 text-emerald-800 border-emerald-300";
  const exact = new Date(iso).toLocaleString("en-GB", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" });
  return { label, cls, days, title: `In this stage for ${label} (since ${exact})` };
}

function thisMonday(d = new Date()) {
  const x = new Date(d);
  const day = x.getUTCDay() || 7;
  x.setUTCDate(x.getUTCDate() - (day - 1));
  return x.toISOString().slice(0, 10);
}

export default function ProductionPlanner({ user }) {
  const [workspaceId, setWorkspaceId] = useState(null);
  const [workspaceRole, setWorkspaceRole] = useState(null);
  const [error, setError] = useState(null);
  const [retrying, setRetrying] = useState(false);
  const [wizardOpen, setWizardOpen] = useState(false);
  const [adhocOpen, setAdhocOpen] = useState(false);
  const [coverStudioOpen, setCoverStudioOpen] = useState(false);
  const [coverLibraryOpen, setCoverLibraryOpen] = useState(false);
  const [wizardPrefill, setWizardPrefill] = useState(null);
  const [selectedCardId, setSelectedCardId] = useState(null);
  const [selectedRenderMode, setSelectedRenderMode] = useState(null);
  const [reloadKey, setReloadKey] = useState(0);
  // REX-110 — search + filters + voice filter (Feb 2026)
  const [searchQ, setSearchQ] = useState("");
  const [voiceFilter, setVoiceFilter] = useState(""); // "" | "remi_personal" | "resiligent_institutional" | "employee_voice"
  // Designer-queue priority filter + sort (Jun 2026). priorityFilter: "" (all)
  // | "1".."4" | "none" (untriaged). sortByPriority floats P1→P4 to the top of
  // each stage column so the designer sees the most urgent work first.
  const [priorityFilter, setPriorityFilter] = useState("");
  const [sortByPriority, setSortByPriority] = useState(true);
  const navigate = useNavigate();
  const location = useLocation();

  // When the user clicks a calendar marker (any country's holiday or
  // content-opportunity tile), open the wizard pre-populated with that
  // occasion. Country flows through to the wizard via the `prefill` prop.
  const openWizardForOccasion = useCallback((mk) => {
    if (!mk) return;
    setWizardPrefill({
      occasion: mk.title || "",
      date: mk.date || "",
      country: (mk.country || "").toUpperCase(),
    });
    setWizardOpen(true);
  }, []);

  // Auto-open the wizard with a prefill when arriving from Today's "Upcoming
  // Occasions" widget. URL: /amplify?occasion=…&date=YYYY-MM-DD&country=GB
  // We strip the params after consuming them so a back-nav doesn't re-trigger.
  useEffect(() => {
    if (!workspaceId) return; // wait until bootstrap
    const sp = new URLSearchParams(location.search);
    const occasion = sp.get("occasion");
    const date = sp.get("date");
    const country = sp.get("country");
    const markerId = sp.get("marker_id");
    if (occasion) {
      setWizardPrefill({
        occasion: occasion.trim(),
        date: date || "",
        country: country || "",
        marker_id: markerId || "",
      });
      setWizardOpen(true);
      // Clean the URL so back/forward / refresh doesn't re-pop the modal.
      const url = location.pathname + (location.hash || "");
      window.history.replaceState({}, "", url);
    }
  }, [workspaceId, location.search, location.pathname, location.hash]);

  // Perf (May 2026) — the Neon Postgres backing v2 lives in a different
  // region from the app container, so this endpoint occasionally hits a
  // transient 502/504/timeout on cold connections. Auto-retry up to 3
  // times with exponential backoff so a single blip doesn't dead-end the
  // whole Amplify surface with "not provisioned".
  const initWs = useCallback(async () => {
    setRetrying(true);
    // Use the shared singleton fetcher (Feb 2026) — coalesces concurrent
    // calls across the whole app, caches for 60s, and applies a unified
    // 3-attempt retry with exponential backoff. Replaces the prior
    // per-component retry loop that compounded into a 7+ request stampede
    // and saturated the Neon worker pool in production.
    try {
      const { fetchMyWorkspaces } = await import("@/lib/workspaceFetch");
      const list = await fetchMyWorkspaces();
      if (!list || !list.length) throw new Error("No v2 workspace membership");
      setWorkspaceId(list[0].workspace_id);
      setWorkspaceRole((list[0].role || "").toLowerCase());
      setError(null);
      setRetrying(false);
      return;
    } catch (e) {
      // Distinguish auth from infra problems
      if (e && (e.status === 401 || e.status === 403)) {
        let stillLoggedIn = false;
        try {
          const meR = await fetch(`${API}/auth/me`, { credentials: "include" });
          stillLoggedIn = meR.ok;
        } catch { stillLoggedIn = false; }
        if (e.status === 403) {
          setError("Your account isn't linked to a workspace yet. Ask an admin to grant you a role.");
        } else if (stillLoggedIn) {
          setError(
            "Couldn't reach the workspace service — your login is fine, but Amplify's database (Neon) likely needs to wake up. Try again in a moment."
          );
        } else {
          setError("Your session has expired. Please log in again.");
        }
        setRetrying(false);
        return;
      }
      setError(
        (e && e.message) ||
        "The workspace service is slow or unavailable. Please refresh in a moment — the database may be warming up."
      );
      setRetrying(false);
    }
  }, []);
  useEffect(() => { initWs(); }, [initWs]);

  if (error) {
    const isSessionExpired = /session has expired/i.test(error);
    return (
      <div className="p-8" data-testid="planner-error">
        <Card className="bg-amber-50 border-amber-300">
          <CardHeader><CardTitle className="text-amber-900 flex items-center gap-2"><AlertTriangle className="h-5 w-5"/> Production planner unavailable</CardTitle></CardHeader>
          <CardContent>
            <p className="text-slate-700 text-sm mb-3">{error}</p>
            {!isSessionExpired && (
              <p className="text-slate-600 text-xs mb-3">
                The external database (Neon) is in a different region from the app, so the first request after an idle period can be slow. If this persists beyond a minute, try a hard refresh (Cmd/Ctrl+Shift+R) or ping an admin.
              </p>
            )}
            <div className="flex gap-2">
              {isSessionExpired ? (
                <button
                  onClick={() => navigate("/login")}
                  data-testid="planner-login-btn"
                  className="inline-flex items-center gap-1 rounded-md bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-medium px-3 py-1.5"
                >
                  Log in again
                </button>
              ) : (
                <button
                  onClick={initWs}
                  disabled={retrying}
                  data-testid="planner-retry-btn"
                  className="inline-flex items-center gap-1 rounded-md bg-amber-600 hover:bg-amber-700 disabled:opacity-60 text-white text-xs font-medium px-3 py-1.5"
                >
                  {retrying ? "Retrying…" : "Try again"}
                </button>
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (!workspaceId) return <div className="p-8"><Skeleton className="h-12 w-1/3"/></div>;

  const onCardClick = (cardId, renderMode = null) => {
    setSelectedCardId(cardId);
    setSelectedRenderMode(renderMode);
  };
  const onMutated = () => setReloadKey((k) => k + 1);

  return (
    <div className="p-6 lg:p-8 space-y-6" data-testid="production-planner">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <p className="text-xs uppercase tracking-[0.2em] text-slate-500">Production</p>
          <h1 className="text-3xl lg:text-4xl font-semibold tracking-tight text-slate-900 mt-1">Planner</h1>
          <p className="text-sm text-slate-600 mt-1">Pipeline · Week · Month — same cards, three lenses.</p>
        </div>
        <div className="flex gap-2">
          <Button
            onClick={() => navigate("/proposals")}
            variant="outline"
            className="border-emerald-300 text-emerald-700 hover:bg-emerald-50"
            data-testid="open-proposals-btn"
          >
            <FileText className="h-4 w-4 mr-2"/>New Proposal
          </Button>
          <Button
            onClick={() => setAdhocOpen(true)}
            variant="outline"
            className="border-teal-300 text-teal-700 hover:bg-teal-50"
            data-testid="open-adhoc-btn"
          >
            <ClipboardList className="h-4 w-4 mr-2"/>Ad-hoc task
          </Button>
          <Button
            onClick={() => setWizardOpen(true)}
            className="bg-indigo-600 hover:bg-indigo-700 text-white"
            data-testid="open-wizard-btn"
          >
            <Sparkles className="h-4 w-4 mr-2"/>Draft with Rex
          </Button>
          <Button
            onClick={() => setCoverStudioOpen(true)}
            variant="outline"
            className="border-indigo-300 text-indigo-700 hover:bg-indigo-50"
            data-testid="open-cover-wizard-btn"
          >
            <FileText className="h-4 w-4 mr-2"/>Create a cover
          </Button>
          <OpenCoverLibraryButton onClick={() => setCoverLibraryOpen(true)} />
        </div>
      </div>

      {/* REX-110: search + voice filter strip */}
      <div className="flex flex-wrap items-center gap-2 bg-white border border-slate-200 rounded-lg px-3 py-2" data-testid="planner-filters">
        <div className="flex items-center gap-1.5 flex-1 min-w-[200px]">
          <Search className="h-4 w-4 text-slate-400" />
          <input
            type="text"
            placeholder="Search title or body…"
            value={searchQ}
            onChange={(e) => setSearchQ(e.target.value)}
            className="flex-1 text-sm bg-transparent border-none outline-none placeholder:text-slate-400"
            data-testid="planner-search-input"
          />
          {searchQ && (
            <button onClick={() => setSearchQ("")} className="text-slate-400 hover:text-slate-700" data-testid="planner-search-clear">
              <X className="h-3.5 w-3.5" />
            </button>
          )}
        </div>
        <div className="h-5 w-px bg-slate-200" />
        <span className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Voice:</span>
        {[
          { id: "", label: "All", cls: "bg-slate-100 text-slate-700 border-slate-300" },
          { id: "remi_personal", label: "Remi", cls: "bg-rose-100 text-rose-800 border-rose-300" },
          { id: "resiligent_institutional", label: "RESILIGENT", cls: "bg-slate-200 text-slate-900 border-slate-400" },
          { id: "employee_voice", label: "Employee", cls: "bg-amber-100 text-amber-900 border-amber-300" },
        ].map((v) => (
          <button
            key={v.id || "all"}
            onClick={() => setVoiceFilter(v.id)}
            className={`text-[10px] uppercase tracking-wider font-bold rounded px-2 py-0.5 border transition ${voiceFilter === v.id ? v.cls + " ring-2 ring-indigo-400" : "bg-white text-slate-500 border-slate-200 hover:bg-slate-50"}`}
            data-testid={`planner-voice-filter-${v.id || "all"}`}
          >{v.label}</button>
        ))}
        <div className="h-5 w-px bg-slate-200" />
        <span className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Priority:</span>
        {[
          { id: "", label: "All", cls: "bg-slate-100 text-slate-700 border-slate-300" },
          { id: "1", label: "P1", cls: PRIORITY_META[1].cls },
          { id: "2", label: "P2", cls: PRIORITY_META[2].cls },
          { id: "3", label: "P3", cls: PRIORITY_META[3].cls },
          { id: "4", label: "P4", cls: PRIORITY_META[4].cls },
          { id: "none", label: "—", cls: "bg-white text-slate-400 border-dashed border-slate-400" },
        ].map((p) => (
          <button
            key={p.id || "all"}
            onClick={() => setPriorityFilter(p.id)}
            title={p.id === "none" ? "Untriaged (no priority)" : (p.id ? PRIORITY_META[p.id]?.title : "All priorities")}
            className={`text-[10px] uppercase tracking-wider font-bold rounded px-2 py-0.5 border transition ${priorityFilter === p.id ? p.cls + " ring-2 ring-indigo-400" : "bg-white text-slate-500 border-slate-200 hover:bg-slate-50"}`}
            data-testid={`planner-priority-filter-${p.id || "all"}`}
          >{p.label}</button>
        ))}
        <button
          onClick={() => setSortByPriority((v) => !v)}
          title="Sort each column by priority (P1 at the top)"
          className={`inline-flex items-center gap-1 text-[10px] uppercase tracking-wider font-bold rounded px-2 py-0.5 border transition ${sortByPriority ? "bg-indigo-100 text-indigo-800 border-indigo-300" : "bg-white text-slate-500 border-slate-200 hover:bg-slate-50"}`}
          data-testid="planner-priority-sort-toggle"
        >
          <ArrowDownUp className="h-3 w-3" /> Sort P1→P4
        </button>
      </div>

      <Tabs defaultValue="production" className="space-y-5">
        <TabsList data-testid="planner-tabs" className="bg-white border border-slate-200">
          <TabsTrigger value="production" data-testid="tab-production"><Kanban className="h-4 w-4 mr-2"/>Production</TabsTrigger>
          <TabsTrigger value="week" data-testid="tab-week"><CalendarDays className="h-4 w-4 mr-2"/>Week</TabsTrigger>
          <TabsTrigger value="calendar" data-testid="tab-calendar"><CalendarRange className="h-4 w-4 mr-2"/>Calendar</TabsTrigger>
          <TabsTrigger value="history" data-testid="amplify-completed-tab" title="Completed / Published archive — filter by week, month, quarter, year, custom"><History className="h-4 w-4 mr-2"/>Completed</TabsTrigger>
        </TabsList>

        <TabsContent value="production"><ProductionTab workspaceId={workspaceId} workspaceRole={workspaceRole} reloadKey={reloadKey} onCardClick={onCardClick} searchQ={searchQ} voiceFilter={voiceFilter} priorityFilter={priorityFilter} sortByPriority={sortByPriority} user={user}/></TabsContent>
        <TabsContent value="week"><WeekTab workspaceId={workspaceId} reloadKey={reloadKey} onCardClick={onCardClick} searchQ={searchQ} voiceFilter={voiceFilter}/></TabsContent>
        <TabsContent value="calendar"><CalendarTab workspaceId={workspaceId} reloadKey={reloadKey} onOccasionClick={openWizardForOccasion}/></TabsContent>
        <TabsContent value="history"><HistoryTab workspaceId={workspaceId} reloadKey={reloadKey} onCardClick={onCardClick}/></TabsContent>
      </Tabs>

      <V2WizardModal
        open={wizardOpen}
        onOpenChange={(v) => { setWizardOpen(v); if (!v) setWizardPrefill(null); }}
        workspaceId={workspaceId}
        prefill={wizardPrefill}
        onCardCreated={() => onMutated()}
      />
      <AdHocTaskModal
        open={adhocOpen}
        onOpenChange={setAdhocOpen}
        workspaceId={workspaceId}
        onCreated={() => onMutated()}
      />
      <CoverStudioModal
        open={coverStudioOpen}
        onOpenChange={setCoverStudioOpen}
        workspaceId={workspaceId}
      />
      <CoverLibraryDrawer
        open={coverLibraryOpen}
        onOpenChange={setCoverLibraryOpen}
        workspaceId={workspaceId}
        onOpenCard={(cardId) => { setCoverLibraryOpen(false); onCardClick(cardId); }}
        onMutated={onMutated}
      />
      <V2CardDrawer
        open={!!selectedCardId && selectedRenderMode !== "template"}
        onOpenChange={(v) => { if (!v) { setSelectedCardId(null); setSelectedRenderMode(null); } }}
        workspaceId={workspaceId}
        cardId={selectedRenderMode !== "template" ? selectedCardId : null}
        user={user}
        onMutated={onMutated}
      />
      <V2TemplateDrawer
        open={!!selectedCardId && selectedRenderMode === "template"}
        onOpenChange={(v) => { if (!v) { setSelectedCardId(null); setSelectedRenderMode(null); } }}
        workspaceId={workspaceId}
        cardId={selectedRenderMode === "template" ? selectedCardId : null}
        onMutated={onMutated}
        onSwitchToDirector={(cid) => { onMutated(); onCardClick(cid, null); }}
      />
    </div>
  );
}

function ProductionTab({ workspaceId, workspaceRole, reloadKey, onCardClick, searchQ = "", voiceFilter = "", priorityFilter = "", sortByPriority = true, user = null }) {
  const [cards, setCards] = useState(null);
  const [refreshSeq, setRefreshSeq] = useState(0);
  const [dragCardId, setDragCardId] = useState(null);
  const [dragOverStage, setDragOverStage] = useState(null);
  // Pending-confirmation drop (for "far" jumps) — { card, fromStage, toStage }.
  const [pendingDrop, setPendingDrop] = useState(null);
  // Mandatory-note prompt for reject / re-submit moves. { card, toStage, kind }
  const [pendingReason, setPendingReason] = useState(null);
  const [reasonText, setReasonText] = useState("");
  // Board layout — May 2026: user asked for horizontal swim lanes since
  // columns "fill up really soon". Persisted per browser via localStorage.
  const [layoutMode, setLayoutMode] = useState(() => {
    if (typeof window === "undefined") return "horizontal";
    return localStorage.getItem("amplify-board-layout") || "horizontal";
  });
  const setAndPersistLayout = (mode) => {
    setLayoutMode(mode);
    try { localStorage.setItem("amplify-board-layout", mode); } catch { /* noop */ }
  };

  // DnD is gated to admin/leader/owner + anyone authorised to approve content
  // (approver role is auto-granted when added to Admin Console > Content Approvers).
  // Anyone signed in can drag cards between PRE-APPROVAL stages.
  // Only users with role==='admin' OR user.can_approve_amplify===true can drop
  // into GATED stages (Approved / Rejected / Scheduled / Published / Completed).
  // Server enforces this too — the UI gate is just a friendly UX layer.
  const canDrag = !!user; // any signed-in user
  const canApprove = !!user && (
    (user.role || "").toLowerCase() === "admin" ||
    user.can_approve_amplify === true ||
    ["admin", "owner", "approver", "leader"].includes((workspaceRole || "").toLowerCase())
  );
  // REX-SOFT-DELETE (Jun 2026) — only approver-level roles may delete cards.
  // Mirrors the backend DELETE_ROLES gate so the button doesn't 403.
  const canDeleteCard = !!user && (
    (user.role || "").toLowerCase() === "admin" ||
    ["owner", "admin", "leader", "approver", "reviewer"].includes((workspaceRole || "").toLowerCase())
  );
  // Editors/designers can delete their own pre-approval work (mirrors backend PRE_APPROVAL_DELETE_STAGES).
  const canWrite = !!user && ["owner", "admin", "editor", "designer", "reviewer", "approver", "leader"].includes((workspaceRole || "").toLowerCase());
  const deleteFor = (c) => (canDeleteCard || (canWrite && PRE_APPROVAL_STAGES.has(c.stage))) ? handleDelete : undefined;

  useEffect(() => {
    setCards(null);
    apiCall(`/v2/workspaces/${workspaceId}/cards?limit=500`)
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error(`cards ${r.status}`))))
      .then(setCards)
      .catch(() => setCards([]));
  }, [workspaceId, reloadKey, refreshSeq]);

  // REX-007 — Sunday auto-empty for the COMPLETED swim lane.
  // Cards that were last touched before this week's Monday get hidden from
  // the live board (they remain in the database and are reachable via the
  // Graveyard view). The cutoff rolls forward each Monday at 00:00 local.
  const weekStartIso = useMemo(() => {
    const d = new Date();
    const day = d.getDay();           // Sun=0, Mon=1, …
    const back = day === 0 ? 6 : day - 1;
    const monday = new Date(d.getFullYear(), d.getMonth(), d.getDate() - back);
    return monday.toISOString();
  }, []);

  // Apply search + voice filters before grouping.
  const filterCards = (list) => {
    if (!list) return list;
    const q = (searchQ || "").trim().toLowerCase();
    return list.filter((c) => {
      if (voiceFilter && c.voice_key !== voiceFilter) return false;
      if (priorityFilter) {
        const p = c.priority ?? null;
        if (priorityFilter === "none") {
          if (p !== null) return false;
        } else if (String(p) !== priorityFilter) {
          return false;
        }
      }
      if (q) {
        const hay = `${c.title || ""} ${c.body_md || ""} ${c.angle || ""}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
  };

  // Group near-duplicates: same normalized title within rolling 30 days.
  const collapseDuplicates = (list) => {
    const buckets = new Map(); // key = normalized title, value = { primary, dupes:[] }
    const result = [];
    list.forEach((c) => {
      const norm = (c.title || "").toLowerCase().replace(/\s+/g, " ").trim().slice(0, 80);
      const key = `${c.stage}::${norm}`;
      const existing = buckets.get(key);
      if (existing) {
        const dt0 = new Date(existing.primary.created_at || existing.primary.updated_at);
        const dt1 = new Date(c.created_at || c.updated_at);
        const dayDiff = Math.abs((dt1 - dt0) / 86400000);
        if (dayDiff <= 30) {
          existing.dupes.push(c);
          return;
        }
      }
      const entry = { primary: c, dupes: [] };
      buckets.set(key, entry);
      result.push(entry);
    });
    return result;
  };

  // Patch in-place when a card is mutated from a child (priority change, etc.).
  // Avoids a full reload — keeps drag state + scroll position intact.
  const handleCardUpdate = (updated) => {
    if (!updated || !updated.card_id) return;
    setCards((prev) => prev ? prev.map((c) => (c.card_id === updated.card_id ? { ...c, ...updated } : c)) : prev);
  };


  const handleDelete = async (card_id, version, opts = {}) => {
    const { skipConfirm = false, silent = false } = opts;
    if (!skipConfirm && !window.confirm("Delete this card? It will be soft-deleted and can be restored from Audit log.")) return;
    try {
      const r = await fetch(`${API}/v2/workspaces/${workspaceId}/cards/${card_id}?expected_version=${version}`, {
        method: "DELETE", credentials: "include",
      });
      if (!r.ok) {
        const e = await r.json().catch(() => ({}));
        throw new Error(e?.detail || `Delete failed (${r.status})`);
      }
      if (!silent) toast.success("Card deleted");
      setRefreshSeq((s) => s + 1);
    } catch (e) {
      if (!silent) toast.error(e.message);
      throw e;
    }
  };

  // Drag-and-drop stage transitions — admin/leader/owner only (server enforces this too).
  // For "far" jumps (more than 1 step apart), confirm first to prevent accidental publishes.
  const stageDistance = (from, to) => {
    const i = STAGE_ORDER.indexOf(from);
    const j = STAGE_ORDER.indexOf(to);
    if (i < 0 || j < 0) return 99;
    return Math.abs(j - i);
  };

  const performTransition = async (card, targetStage, note = "Drag-and-drop") => {
    // Optimistic: stamp the new stage immediately.
    setCards((prev) => prev.map((c) => (c.card_id === card.card_id ? { ...c, stage: targetStage } : c)));
    try {
      const r = await fetch(`${API}/v2/workspaces/${workspaceId}/cards/${card.card_id}/transition`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          target_stage: targetStage,
          expected_version: card.version,
          note,
          force: true, // admin/leader/owner can skip the state machine
        }),
      });
      if (!r.ok) {
        const e = await r.json().catch(() => ({}));
        throw new Error(typeof e?.detail === "string" ? e.detail : `Transition failed (${r.status})`);
      }
      toast.success(`Moved to ${targetStage.replace(/_/g, " ")}`);
      setRefreshSeq((s) => s + 1);
    } catch (e) {
      toast.error(e.message);
      setRefreshSeq((s) => s + 1); // roll back optimistic update
    }
  };

  const handleDrop = (targetStage) => {
    setDragOverStage(null);
    const cardId = dragCardId;
    setDragCardId(null);
    if (!cardId || !cards) return;
    const card = cards.find((c) => c.card_id === cardId);
    if (!card || card.stage === targetStage) return;
    // Permission check: gated stages need approver rights.
    if (GATED_STAGES.has(targetStage) && !canApprove) {
      toast.error(
        `Only approvers can move cards to ${STAGE_LABELS[targetStage] || targetStage}. Ask an admin to grant you approval rights.`
      );
      return;
    }
    // Mandatory-note transitions (Jun 2026) take priority over the far-jump
    // confirm: a rejection always needs a reason; a re-submit from Rejected
    // always needs a "what I fixed" note.
    if (targetStage === "REJECTED") {
      setPendingReason({ card, toStage: "REJECTED", kind: "reject" });
      return;
    }
    if (card.stage === "REJECTED" && targetStage === "PENDING_APPROVAL") {
      setPendingReason({ card, toStage: "PENDING_APPROVAL", kind: "fix" });
      return;
    }
    // Far jump — confirm. (1 step = same column or adjacent, no confirm.)
    if (stageDistance(card.stage, targetStage) > 1) {
      setPendingDrop({ card, fromStage: card.stage, toStage: targetStage });
      return;
    }
    performTransition(card, targetStage);
  };

  // Memoised filter + bucket so re-renders due to drag/drop state don't re-run
  // the O(n*S) duplicate-grouping work each time. Hooks MUST be called before
  // any early return — keep them up here, default to [] when cards is null.
  const filteredCards = useMemo(() => filterCards(cards || []), [cards, searchQ, voiceFilter, priorityFilter]);  // eslint-disable-line react-hooks/exhaustive-deps
  const byStage = useMemo(() => {
    const out = {};
    STAGE_ORDER.forEach((s) => { out[s] = []; });
    filteredCards.forEach((c) => {
      if (!out[c.stage]) return;
      // REX-007 — Sunday auto-empty: hide COMPLETED cards whose last activity
      // is older than this week's Monday. They live on in the Graveyard.
      if (c.stage === "COMPLETED" && c.updated_at && c.updated_at < weekStartIso) return;
      out[c.stage].push(c);
    });
    // Float higher priorities (P1→P4) to the top of each column; untriaged
    // (null) sinks to the bottom. Stable sort preserves the API order within
    // the same priority. Toggle off to restore the default order.
    if (sortByPriority) {
      STAGE_ORDER.forEach((s) => {
        out[s] = out[s]
          .map((c, i) => [c, i])
          .sort((a, b) => ((a[0].priority ?? 99) - (b[0].priority ?? 99)) || (a[1] - b[1]))
          .map((pair) => pair[0]);
      });
    }
    return out;
  }, [filteredCards, weekStartIso, sortByPriority]);  // eslint-disable-line react-hooks/exhaustive-deps
  const groupedByStage = useMemo(() => {
    const out = {};
    STAGE_ORDER.forEach((s) => { out[s] = collapseDuplicates(byStage[s] || []); });
    return out;
  }, [byStage]);  // eslint-disable-line react-hooks/exhaustive-deps

  if (!cards) return <Skeleton className="h-64"/>;

  const isHorizontal = layoutMode === "horizontal";

  return (
    <TooltipProvider delayDuration={150}>
    {/* Layout toggle */}
    <div className="flex items-center justify-end mb-2 gap-1" data-testid="board-layout-toggle">
      <span className="text-[10px] uppercase tracking-wide text-slate-500 mr-1">Layout</span>
      <button
        type="button"
        onClick={() => setAndPersistLayout("horizontal")}
        data-testid="board-layout-horizontal"
        className={`px-2 py-1 text-[11px] rounded-md border transition-colors ${
          isHorizontal ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-700 border-slate-300 hover:bg-slate-50"
        }`}
        title="Each stage is a row, cards flow left-to-right"
      >
        Horizontal lanes
      </button>
      <button
        type="button"
        onClick={() => setAndPersistLayout("vertical")}
        data-testid="board-layout-vertical"
        className={`px-2 py-1 text-[11px] rounded-md border transition-colors ${
          !isHorizontal ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-700 border-slate-300 hover:bg-slate-50"
        }`}
        title="Each stage is a column, cards stack vertically"
      >
        Vertical columns
      </button>
    </div>

    <div
      className={isHorizontal
        ? "flex flex-col gap-2"
        : "grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3"}
      data-testid="production-board"
      data-layout={layoutMode}
    >
      {STAGE_ORDER.map((stage) => {
        const grouped = groupedByStage[stage] || [];
        const isDropTarget = dragOverStage === stage;
        const stageLabel = STAGE_LABELS[stage] || stage.replace(/_/g, " ");
        const isGated = GATED_STAGES.has(stage);
        const userCanDropHere = canDrag && (!isGated || canApprove);
        const laneBg = STAGE_LANE_BG[stage] || "bg-white";
        const headerBg = STAGE_HEADER_BG[stage] || "bg-slate-100 text-slate-800 border-b-slate-300";

        if (isHorizontal) {
          // ── Horizontal swim lane ──────────────────────────────────────
          return (
            <Card
              key={stage}
              className={`${laneBg} border-slate-200 shadow-sm transition flex flex-row ${isDropTarget ? "ring-2 ring-indigo-400" : ""} ${isGated && !canApprove && dragCardId ? "opacity-70" : ""}`}
              data-testid={`stage-col-${stage.toLowerCase()}`}
              onDragOver={userCanDropHere ? (e) => { if (dragCardId) { e.preventDefault(); setDragOverStage(stage); } } : undefined}
              onDragLeave={userCanDropHere ? () => setDragOverStage((s) => (s === stage ? null : s)) : undefined}
              onDrop={canDrag ? (e) => { e.preventDefault(); handleDrop(stage); } : undefined}
            >
              <div className={`flex flex-col items-stretch justify-start py-2 px-3 border-r ${headerBg} min-w-[160px] max-w-[160px]`}>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <span className="cursor-help underline-offset-2 decoration-dotted underline flex items-center gap-1 text-xs uppercase tracking-wider font-semibold">
                      {stageLabel}
                      {isGated && (
                        <span title="Approver-only column" className="text-[9px] opacity-70" data-testid={`stage-lock-${stage.toLowerCase()}`}>🔒</span>
                      )}
                    </span>
                  </TooltipTrigger>
                  <TooltipContent side="right" className="max-w-[240px] text-xs">
                    {STAGE_DESCRIPTIONS[stage]}
                  </TooltipContent>
                </Tooltip>
                <Badge variant="outline" className="bg-white/70 text-current border-current/30 mt-1 self-start text-[10px]">
                  {byStage[stage].length}
                </Badge>
              </div>
              <div className="flex-1 overflow-x-auto px-3 py-2 min-h-[80px]">
                {grouped.length === 0 ? (
                  <p className="text-xs text-slate-400 italic py-2">— empty —</p>
                ) : (
                  <div className="flex gap-2 items-stretch">
                    {grouped.map(({ primary, dupes }) => (
                      <div key={primary.card_id} className="min-w-[220px] max-w-[260px] flex-shrink-0">
                        <CardChip
                          c={primary}
                          dupes={dupes}
                          onClick={onCardClick}
                          onDelete={deleteFor(primary)}
                          onDragStart={canDrag ? (id) => setDragCardId(id) : undefined}
                          onDragEnd={canDrag ? () => { setDragCardId(null); setDragOverStage(null); } : undefined}
                          isDragging={dragCardId === primary.card_id}
                          workspaceId={workspaceId}
                          onCardUpdate={handleCardUpdate}
                        />
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </Card>
          );
        }

        // ── Vertical column (legacy) ──────────────────────────────────
        return (
        <Card
          key={stage}
          className={`${laneBg} border-slate-200 shadow-sm transition ${isDropTarget ? "ring-2 ring-indigo-400" : ""} ${isGated && !canApprove && dragCardId ? "opacity-70" : ""}`}
          data-testid={`stage-col-${stage.toLowerCase()}`}
          onDragOver={userCanDropHere ? (e) => { if (dragCardId) { e.preventDefault(); setDragOverStage(stage); } } : undefined}
          onDragLeave={userCanDropHere ? () => setDragOverStage((s) => (s === stage ? null : s)) : undefined}
          onDrop={canDrag ? (e) => { e.preventDefault(); handleDrop(stage); } : undefined}
        >
          <CardHeader className={`py-3 px-3 border-b ${headerBg}`}>
            <CardTitle className="text-xs uppercase tracking-wider flex justify-between items-center">
              <Tooltip>
                <TooltipTrigger asChild>
                  <span className="cursor-help underline-offset-2 decoration-dotted underline flex items-center gap-1">
                    {stageLabel}
                    {isGated && (
                      <span title="Approver-only column" className="text-[9px] opacity-70" data-testid={`stage-lock-${stage.toLowerCase()}`}>🔒</span>
                    )}
                  </span>
                </TooltipTrigger>
                <TooltipContent side="top" className="max-w-[240px] text-xs">
                  {STAGE_DESCRIPTIONS[stage]}
                </TooltipContent>
              </Tooltip>
              <Badge variant="outline" className="bg-white/70 text-current border-current/30">{byStage[stage].length}</Badge>
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-2 max-h-[60vh] overflow-y-auto px-3 pb-3 min-h-[60px]">
            {grouped.length === 0 ? (
              <p className="text-xs text-slate-400 italic text-center py-2">— empty —</p>
            ) : grouped.map(({ primary, dupes }) => (
              <CardChip
                key={primary.card_id}
                c={primary}
                dupes={dupes}
                onClick={onCardClick}
                onDelete={deleteFor(primary)}
                onDragStart={canDrag ? (id) => setDragCardId(id) : undefined}
                onDragEnd={canDrag ? () => { setDragCardId(null); setDragOverStage(null); } : undefined}
                isDragging={dragCardId === primary.card_id}
              />
            ))}
          </CardContent>
        </Card>
        );
      })}
    </div>

    {/* Far-jump confirmation dialog (only shown for stage distance > 1) */}
    {pendingDrop && (
      <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" data-testid="far-jump-confirm">
        <Card className="bg-white max-w-md w-full">
          <CardHeader>
            <CardTitle className="text-base text-slate-900 flex items-center gap-2">
              <AlertTriangle className="h-4 w-4 text-amber-500" /> Skip stages?
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <p className="text-sm text-slate-700">
              You're moving "<span className="font-semibold">{pendingDrop.card.title}</span>" from
              {" "}<span className="font-mono bg-slate-100 px-1 rounded">{pendingDrop.fromStage}</span>
              {" "}straight to{" "}
              <span className="font-mono bg-slate-100 px-1 rounded">{pendingDrop.toStage}</span>.
            </p>
            <p className="text-xs text-amber-700">
              This skips intermediate review/approval. Use for cases like "I already published this manually — mark it done."
            </p>
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" onClick={() => setPendingDrop(null)} data-testid="far-jump-cancel">
                Cancel
              </Button>
              <Button
                onClick={() => {
                  performTransition(pendingDrop.card, pendingDrop.toStage);
                  setPendingDrop(null);
                }}
                className="bg-amber-600 hover:bg-amber-700 text-white"
                data-testid="far-jump-confirm-btn"
              >
                Yes, skip stages
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    )}

    {/* Mandatory-note dialog: rejection reason OR "what I fixed" on re-submit. */}
    {pendingReason && (
      <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" data-testid="reason-modal">
        <Card className="bg-white max-w-md w-full">
          <CardHeader>
            <CardTitle className="text-base text-slate-900 flex items-center gap-2">
              {pendingReason.kind === "reject" ? (
                <><AlertTriangle className="h-4 w-4 text-red-500" /> Why is this rejected?</>
              ) : (
                <><AlertTriangle className="h-4 w-4 text-emerald-600" /> What did you fix?</>
              )}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <p className="text-sm text-slate-700">
              {pendingReason.kind === "reject"
                ? <>Tell the designer why "<span className="font-semibold">{pendingReason.card.title}</span>" was rejected. This is saved to the card's stage history.</>
                : <>Before re-submitting "<span className="font-semibold">{pendingReason.card.title}</span>" for approval, describe what you changed. This is saved to the card's stage history.</>}
            </p>
            <textarea
              autoFocus
              value={reasonText}
              onChange={(e) => setReasonText(e.target.value)}
              rows={4}
              placeholder={pendingReason.kind === "reject"
                ? "e.g. Logo is too small and the headline overlaps the photo — please rework."
                : "e.g. Enlarged the logo, moved the headline above the image, fixed the brand blue."}
              className="w-full border border-slate-300 rounded-md p-2 text-sm focus:outline-none focus:ring-2 focus:ring-slate-400"
              data-testid="reason-textarea"
            />
            <div className="flex justify-end gap-2 pt-1">
              <Button
                variant="outline"
                onClick={() => { setPendingReason(null); setReasonText(""); }}
                data-testid="reason-cancel"
              >
                Cancel
              </Button>
              <Button
                disabled={!reasonText.trim()}
                onClick={() => {
                  performTransition(pendingReason.card, pendingReason.toStage, reasonText.trim());
                  setPendingReason(null);
                  setReasonText("");
                }}
                className={pendingReason.kind === "reject"
                  ? "bg-red-600 hover:bg-red-700 text-white"
                  : "bg-emerald-600 hover:bg-emerald-700 text-white"}
                data-testid="reason-submit"
              >
                {pendingReason.kind === "reject" ? "Reject card" : "Re-submit for approval"}
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    )}
    </TooltipProvider>
  );
}

function WeekTab({ workspaceId, reloadKey, onCardClick, searchQ = "", voiceFilter = "" }) {
  const [start, setStart] = useState(() => thisMonday());
  const [data, setData] = useState(null);
  useEffect(() => {
    setData(null);
    apiCall(`/v2/workspaces/${workspaceId}/week?start=${start}`).then((r) => r.json()).then(setData);
  }, [workspaceId, start, reloadKey]);

  const shift = (delta) => {
    const d = new Date(start + "T00:00:00Z");
    d.setUTCDate(d.getUTCDate() + delta * 7);
    setStart(d.toISOString().slice(0, 10));
  };

  if (!data) return <Skeleton className="h-64"/>;
  // REX-110: client-side filter on title/body and voice
  const matches = (c) => {
    if (voiceFilter && c.voice_key !== voiceFilter) return false;
    const q = (searchQ || "").trim().toLowerCase();
    if (!q) return true;
    return `${c.title || ""} ${c.body_md || ""} ${c.angle || ""}`.toLowerCase().includes(q);
  };
  const filteredDays = Object.fromEntries(
    Object.entries(data.days).map(([day, list]) => [day, (list || []).filter(matches)])
  );
  const filteredUnplanned = (data.unplanned || []).filter(matches);
  return (
    <div data-testid="week-planner">
      <div className="flex items-center gap-3 mb-4 flex-wrap">
        <Button variant="outline" size="sm" onClick={() => shift(-1)} data-testid="week-prev"><ChevronLeft className="h-4 w-4"/></Button>
        <p className="text-slate-900 text-sm font-medium">{fmtDate(data.start)} → {fmtDate(data.end)}</p>
        <Button variant="outline" size="sm" onClick={() => shift(1)} data-testid="week-next"><ChevronRight className="h-4 w-4"/></Button>
        <Button variant="ghost" size="sm" onClick={() => setStart(thisMonday())} data-testid="week-today" className="text-slate-700">This week</Button>
        <span className="text-xs text-slate-500 ml-2">{data.totals.scheduled} scheduled · {data.totals.unplanned} unplanned</span>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-7 gap-3">
        {Object.entries(filteredDays).map(([day, list]) => (
          <Card key={day} className="bg-white border-slate-200 shadow-sm" data-testid={`week-day-${day}`}>
            <CardHeader className="py-2 px-3"><CardTitle className="text-[11px] uppercase tracking-wider text-slate-600">{fmtDate(day)}</CardTitle></CardHeader>
            <CardContent className="space-y-2 min-h-[80px] max-h-[40vh] overflow-y-auto px-3 pb-3">
              {list.length === 0 ? <p className="text-[11px] text-slate-400">—</p>
                : list.map((c) => <CardChip key={c.card_id} c={c} onClick={onCardClick} workspaceId={workspaceId}/>)}
            </CardContent>
          </Card>
        ))}
      </div>
      {filteredUnplanned.length > 0 && (
        <Card className="bg-white border-slate-200 shadow-sm mt-4" data-testid="week-unplanned">
          <CardHeader><CardTitle className="text-slate-800 text-sm">Unplanned active cards ({filteredUnplanned.length})</CardTitle></CardHeader>
          <CardContent className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
            {filteredUnplanned.map((c) => <CardChip key={c.card_id} c={c} onClick={onCardClick} workspaceId={workspaceId}/>)}
          </CardContent>
        </Card>
      )}
    </div>
  );
}

function CalendarTab({ workspaceId, reloadKey, onOccasionClick }) {
  const [month, setMonth] = useState(() => new Date().toISOString().slice(0, 7));
  const [data, setData] = useState(null);
  const [markers, setMarkers] = useState({}); // date → [marker, ...]
  useEffect(() => {
    setData(null);
    apiCall(`/v2/workspaces/${workspaceId}/calendar?month=${month}`).then((r) => r.json()).then(setData);
  }, [workspaceId, month, reloadKey]);
  useEffect(() => {
    // Overlay holiday + national-day markers on the month grid. Pulled
    // from the Mongo-side content-calendar service (NG / UK / Ghana /
    // Kenya / SA / Pan-African + any custom dates the user added).
    fetch(`${API}/content-calendar/markers`, { credentials: "include" })
      .then((r) => (r.ok ? r.json() : { markers: [] }))
      .then((body) => {
        const grouped = {};
        for (const m of (body.markers || [])) {
          if (!m.date) continue;
          (grouped[m.date] = grouped[m.date] || []).push(m);
        }
        setMarkers(grouped);
      })
      .catch(() => setMarkers({}));
  }, [workspaceId, month]);
  const shift = (delta) => {
    const [y, m] = month.split("-").map(Number);
    const d = new Date(Date.UTC(y, m - 1 + delta, 1));
    setMonth(d.toISOString().slice(0, 7));
  };
  if (!data) return <Skeleton className="h-64"/>;
  const [y, m] = data.month.split("-").map(Number);
  const first = new Date(Date.UTC(y, m - 1, 1));
  const lead = (first.getUTCDay() || 7) - 1;
  const daysInMonth = new Date(Date.UTC(y, m, 0)).getUTCDate();
  const cells = [];
  for (let i = 0; i < lead; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) {
    const iso = `${data.month}-${String(d).padStart(2, "0")}`;
    cells.push({ iso, day: d, list: data.by_day[iso] || [], markers: markers[iso] || [] });
  }
  while (cells.length % 7 !== 0) cells.push(null);

  // Tint tone for a cell that has a marker — amber for opportunities,
  // slate for regular public holidays.
  const cellTone = (marks) => {
    if (!marks || marks.length === 0) return "bg-white border-slate-200";
    const hasOpp = marks.some((m) => m.content_opportunity);
    return hasOpp
      ? "bg-amber-50/70 border-amber-200"
      : "bg-slate-50 border-slate-300";
  };

  return (
    <div data-testid="month-calendar">
      <div className="flex items-center gap-3 mb-4">
        <Button variant="outline" size="sm" onClick={() => shift(-1)} data-testid="month-prev"><ChevronLeft className="h-4 w-4"/></Button>
        <p className="text-slate-900 text-sm font-medium">{data.month}</p>
        <Button variant="outline" size="sm" onClick={() => shift(1)} data-testid="month-next"><ChevronRight className="h-4 w-4"/></Button>
        <span className="text-xs text-slate-500 ml-2">{data.total_cards} cards · {Object.values(markers).flat().filter((m) => m.date?.startsWith(data.month)).length} holidays this month</span>
      </div>
      <div className="grid grid-cols-7 gap-1 text-[11px] uppercase tracking-wider text-slate-500 mb-2">
        {["Mon","Tue","Wed","Thu","Fri","Sat","Sun"].map((d) => <div key={d} className="px-2">{d}</div>)}
      </div>
      <div className="grid grid-cols-7 gap-1">
        {cells.map((cell, idx) => (
          <div key={idx} className={`min-h-[90px] rounded-md border ${cell ? cellTone(cell.markers) : "border-transparent"} p-1.5`}
               data-testid={cell ? `cal-day-${cell.iso}` : undefined}>
            {cell && (
              <>
                <p className="text-[11px] text-slate-500 mb-1 font-medium flex items-center justify-between">
                  <span>{cell.day}</span>
                  {cell.markers.length > 0 && (
                    <span className="text-[9px] px-1 rounded bg-amber-100 text-amber-700" data-testid={`cal-marker-${cell.iso}`}>
                      {cell.markers.length === 1 ? cell.markers[0].country?.slice(0, 3) || "★" : `${cell.markers.length}`}
                    </span>
                  )}
                </p>
                {cell.markers.slice(0, 1).map((mk) => {
                  const clickable = typeof onOccasionClick === "function";
                  return (
                    <button
                      key={mk.marker_id}
                      type="button"
                      onClick={clickable ? () => onOccasionClick({ ...mk, date: mk.date || cell.iso }) : undefined}
                      className={`group w-full text-left text-[9px] truncate rounded px-1 py-0.5 mb-0.5 flex items-center gap-1 ${mk.content_opportunity ? "bg-amber-200 text-amber-900 hover:bg-amber-300" : "bg-slate-200 text-slate-700 hover:bg-slate-300"} ${clickable ? "cursor-pointer transition-colors" : ""}`}
                      title={clickable ? `Draft a post for: ${mk.title}${mk.country ? ` (${mk.country})` : ""}` : `${mk.title}${mk.country ? ` (${mk.country})` : ""}`}
                      data-testid={`cal-holiday-${cell.iso}`}
                    >
                      <span className="truncate flex-1">{mk.title}</span>
                      {clickable && (
                        <Sparkles className="h-2.5 w-2.5 opacity-0 group-hover:opacity-100 transition-opacity flex-none" />
                      )}
                    </button>
                  );
                })}
                <div className="space-y-1">
                  {cell.list.slice(0, 3).map((c) => (
                    <div key={c.card_id} className={`text-[10px] truncate rounded px-1.5 py-0.5 border font-medium ${STAGE_COLORS[c.stage] || "bg-slate-100 text-slate-800 border-slate-300"}`}>
                      {c.title}
                    </div>
                  ))}
                  {cell.list.length > 3 && <p className="text-[10px] text-slate-500">+{cell.list.length - 3} more</p>}
                </div>
              </>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

function CardChip({ c, onClick, dupes = [], onDelete, onDragStart, onDragEnd, isDragging, workspaceId, onCardUpdate }) {
  const dupeCount = dupes.length;
  const clickable = typeof onClick === "function";
  const draggable = typeof onDragStart === "function";
  const voice = VOICE_BADGE[c.voice_key];
  const isEmployee = c.post_type === "employee";
  const isBespoke = c.render_mode === "bespoke";
  const tis = timeInStage(c.stage_entered_at || c.updated_at);
  const [showDupes, setShowDupes] = useState(false);
  const [bulkDeleting, setBulkDeleting] = useState(false);
  const [switchingTpl, setSwitchingTpl] = useState(false);
  const canUseTemplate = ["SOCIAL_POST", "POST"].includes(c.card_type) && c.render_mode !== "template";
  const useTemplate = async (e) => {
    e.stopPropagation();
    setSwitchingTpl(true);
    try {
      const r = await fetch(`${API}/v2/workspaces/${workspaceId}/cards/${c.card_id}`, {
        method: "PATCH",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ render_mode: "template", expected_version: c.version }),
      });
      if (!r.ok && r.status !== 409) throw new Error("patch failed");
      onClick && onClick(c.card_id, "template");
    } catch {
      toast.error("Couldn't open the template editor");
    } finally {
      setSwitchingTpl(false);
    }
  };
  const stopAndDelete = (e) => {
    e.stopPropagation();
    onDelete && onDelete(c.card_id, c.version);
  };
  const onDupesBadgeClick = (e) => {
    e.stopPropagation();
    setShowDupes((v) => !v);
  };
  const onBulkDelete = async (e) => {
    e.stopPropagation();
    if (!onDelete) return;
    if (!window.confirm(`Delete all ${dupeCount} duplicate version${dupeCount === 1 ? "" : "s"} of "${c.title.slice(0, 60)}"? The primary card will be kept.`)) return;
    setBulkDeleting(true);
    try {
      // sequential to keep audit log readable; small N (typically <10)
      for (const d of dupes) {
        try { await onDelete(d.card_id, d.version, { skipConfirm: true, silent: true }); } catch { /* keep going */ }
      }
      toast.success(`Deleted ${dupeCount} duplicate${dupeCount === 1 ? "" : "s"}`);
      setShowDupes(false);
    } finally {
      setBulkDeleting(false);
    }
  };
  return (
    <div
      className={`group relative rounded-md border ${STAGE_COLORS[c.stage] || "bg-slate-100 border-slate-300 text-slate-800"} ${isBespoke ? "border-dashed border-2 border-amber-500 ring-1 ring-amber-300/60" : ""} p-2 shadow-sm ${clickable ? "cursor-pointer hover:ring-2 hover:ring-indigo-300 transition" : ""} ${isDragging ? "opacity-40" : ""}`}
      data-testid={`chip-${c.card_id}`}
      data-render-mode={c.render_mode || "template"}
      title={c.title}
      onClick={clickable ? () => onClick(c.card_id, c.render_mode) : undefined}
      role={clickable ? "button" : undefined}
      draggable={draggable}
      onDragStart={draggable ? (e) => {
        e.dataTransfer.effectAllowed = "move";
        e.dataTransfer.setData("text/plain", c.card_id);
        onDragStart(c.card_id);
      } : undefined}
      onDragEnd={draggable ? onDragEnd : undefined}
    >
      {onDelete && (
        <button
          type="button"
          onClick={stopAndDelete}
          className="absolute top-1 right-1 opacity-0 group-hover:opacity-100 transition rounded-full bg-white/80 hover:bg-red-100 border border-slate-300 hover:border-red-400 p-0.5"
          aria-label="Delete card"
          data-testid={`chip-delete-${c.card_id}`}
        >
          <X className="h-3 w-3 text-slate-600 hover:text-red-700" />
        </button>
      )}
      {c.image_url && (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            try {
              const u = new URL(c.image_url, window.location.origin);
              u.searchParams.set("download", "true");
              const safeTitle = (c.title || "amplify-card")
                .replace(/[^a-zA-Z0-9-_ ]/g, "")
                .trim()
                .replace(/\s+/g, "-")
                .slice(0, 60) || "amplify-card";
              const fmt = c.image_overlay?.format || "square_1080";
              u.searchParams.set("filename", `${safeTitle}__${fmt}.png`);
              const a = document.createElement("a");
              a.href = u.toString();
              a.download = `${safeTitle}__${fmt}.png`;
              document.body.appendChild(a);
              a.click();
              document.body.removeChild(a);
            } catch {
              window.open(c.image_url, "_blank");
            }
          }}
          className="absolute top-1 right-6 opacity-0 group-hover:opacity-100 transition rounded-full bg-white/80 hover:bg-emerald-100 border border-slate-300 hover:border-emerald-400 p-0.5 text-[9px] leading-none px-1"
          aria-label="Download PNG"
          data-testid={`chip-download-${c.card_id}`}
          title="Download PNG"
        >⬇</button>
      )}

      <p className="text-xs font-medium leading-tight line-clamp-2 pr-4">{c.title}</p>

      {/* Time-in-stage badge — how long this card has sat in its current stage. */}
      {tis && (
        <div className="mt-1.5">
          <span
            className={`inline-flex items-center gap-1 text-[9px] font-semibold rounded px-1.5 py-[1px] border ${tis.cls}`}
            data-testid={`chip-stage-age-${c.card_id}`}
            title={tis.title}
          >
            <Clock className="h-2.5 w-2.5" />
            {tis.label} in stage
          </span>
        </div>
      )}

      {/* Voice + author attribution row */}
      {(voice || isEmployee || isBespoke || dupeCount > 0) && (
        <div className="flex flex-wrap gap-1 mt-1.5">
          {isBespoke && (
            <span className="text-[9px] uppercase tracking-wider font-bold rounded px-1.5 py-[1px] border bg-amber-100 text-amber-900 border-amber-400" data-testid={`chip-bespoke-${c.card_id}`}>
              ✎ Bespoke
            </span>
          )}
          {voice && (
            <span className={`text-[9px] uppercase tracking-wider font-bold rounded px-1.5 py-[1px] border ${voice.cls}`} data-testid={`chip-voice-${c.card_id}`}>
              {voice.label}
            </span>
          )}
          {isEmployee && c.posted_for_name && (
            <span className="text-[9px] rounded px-1.5 py-[1px] border bg-amber-100 text-amber-900 border-amber-300" data-testid={`chip-postedfor-${c.card_id}`}>
              for: {c.posted_for_name.split(" ")[0]}
            </span>
          )}          {dupeCount > 0 && (
            <button
              type="button"
              onClick={onDupesBadgeClick}
              className="text-[9px] rounded px-1.5 py-[1px] border bg-indigo-100 text-indigo-800 border-indigo-300 inline-flex items-center gap-0.5 hover:bg-indigo-200 transition cursor-pointer"
              data-testid={`chip-dupes-${c.card_id}`}
              title="Click to see all duplicate versions"
            >
              <Layers className="h-2.5 w-2.5" />{dupeCount + 1} versions
            </button>
          )}
        </div>
      )}

      {/* Image provenance stamp — Rex-designed vs manually uploaded graphic */}
      {c.image_url && c.image_overlay?.image_provenance && (
        <div className="mt-1.5">
          {c.image_overlay.image_provenance === "rex_designed" ? (
            <span
              className="inline-flex items-center gap-1 text-[9px] uppercase tracking-wider font-bold rounded px-1.5 py-[1px] border bg-indigo-100 text-indigo-800 border-indigo-300"
              data-testid={`chip-provenance-${c.card_id}`}
              title="This graphic was designed in Rex"
            >
              <Sparkles className="h-2.5 w-2.5" /> REX Designed
            </span>
          ) : (
            <span
              className="inline-flex items-center gap-1 text-[9px] uppercase tracking-wider font-bold rounded px-1.5 py-[1px] border bg-slate-100 text-slate-700 border-slate-300"
              data-testid={`chip-provenance-${c.card_id}`}
              title="This graphic was manually uploaded"
            >
              <Upload className="h-2.5 w-2.5" /> Uploaded
            </span>
          )}
        </div>
      )}


      <div className="flex justify-between items-center mt-1.5 gap-1">
        <div className="flex items-center gap-1 min-w-0">
          {workspaceId && (
            <CardPriorityBadge
              card={c}
              workspaceId={workspaceId}
              size="sm"
              onChange={(updated) => onCardUpdate && onCardUpdate(updated)}
            />
          )}
          <span className="text-[10px] uppercase tracking-wider opacity-80 font-semibold truncate">{c.card_type === "ADHOC" ? "AD-HOC TASK" : c.card_type}</span>
        </div>
        {c.due_date && <span className="text-[10px] opacity-80 font-medium flex-shrink-0">{c.due_date.slice(5)}</span>}
      </div>

      {canUseTemplate && clickable && (
        <button
          type="button"
          onClick={useTemplate}
          disabled={switchingTpl}
          data-testid={`chip-use-template-${c.card_id}`}
          className="mt-1.5 w-full flex items-center justify-center gap-1 rounded bg-white/70 hover:bg-white border border-slate-300 hover:border-indigo-400 text-[10px] font-semibold text-slate-700 hover:text-indigo-700 py-1 transition opacity-0 group-hover:opacity-100"
          title="Open this post in the Template editor (cut-out subjects, stock photos, on-brand backgrounds)"
        >
          <Layers className="h-3 w-3" /> {switchingTpl ? "Opening…" : "Use a template"}
        </button>
      )}

      {/* Versions popover (REX-103/104) */}
      {showDupes && dupeCount > 0 && (
        <div
          className="absolute z-30 left-0 right-0 top-full mt-1 bg-white border border-slate-300 rounded-lg shadow-xl p-2 text-slate-900"
          onClick={(e) => e.stopPropagation()}
          data-testid={`chip-dupes-popover-${c.card_id}`}
        >
          <div className="flex items-center justify-between mb-1.5 pb-1.5 border-b border-slate-200">
            <p className="text-[10px] uppercase tracking-wider font-bold text-slate-700">All versions ({dupeCount + 1})</p>
            <button onClick={() => setShowDupes(false)} className="text-slate-400 hover:text-slate-700">
              <X className="h-3 w-3" />
            </button>
          </div>
          <ul className="space-y-1 max-h-48 overflow-y-auto">
            <li className="flex items-center gap-1.5 px-1 py-0.5 rounded bg-emerald-50">
              <span className="text-[9px] font-bold text-emerald-700 uppercase tracking-wider w-12 flex-shrink-0">Primary</span>
              <span className="text-xs flex-1 truncate" title={c.title}>{c.title}</span>
              <span className="text-[10px] text-slate-500 flex-shrink-0">{c.created_at ? c.created_at.slice(0, 10) : ""}</span>
            </li>
            {dupes.map((d) => (
              <li key={d.card_id} className="flex items-center gap-1.5 px-1 py-0.5 rounded hover:bg-slate-50" data-testid={`chip-dupes-row-${d.card_id}`}>
                <span className="text-[9px] text-slate-500 uppercase tracking-wider w-12 flex-shrink-0">Dup</span>
                <button
                  type="button"
                  className="text-xs flex-1 truncate text-left hover:text-indigo-600"
                  onClick={() => clickable && onClick(d.card_id, d.render_mode)}
                  title={d.title}
                >{d.title}</button>
                <span className="text-[10px] text-slate-500 flex-shrink-0">{d.created_at ? d.created_at.slice(0, 10) : ""}</span>
                {onDelete && (
                  <button
                    type="button"
                    className="text-rose-500 hover:text-rose-700 flex-shrink-0"
                    onClick={(e) => { e.stopPropagation(); onDelete(d.card_id, d.version); }}
                    aria-label="Delete this duplicate"
                    data-testid={`chip-dupes-delete-${d.card_id}`}
                  >
                    <X className="h-3 w-3" />
                  </button>
                )}
              </li>
            ))}
          </ul>
          {onDelete && dupeCount > 0 && (
            <div className="mt-2 pt-2 border-t border-slate-200">
              <button
                type="button"
                onClick={onBulkDelete}
                disabled={bulkDeleting}
                className="w-full text-[11px] font-semibold rounded bg-rose-600 hover:bg-rose-700 disabled:opacity-50 text-white px-2 py-1.5 transition"
                data-testid={`chip-dupes-bulk-delete-${c.card_id}`}
              >
                {bulkDeleting ? "Deleting…" : `Delete all ${dupeCount} duplicate${dupeCount === 1 ? "" : "s"}`}
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}



// ── History (Completed / Published / Approved over a period) ──────────────
// Shows finalised cards with a date-range filter. May 2026 — user asked for
// a "separate view for what was completed over a period of time we should
// be able to filter". Now powered by the shared <CompletedHistoryPanel>
// + sparkline.
function HistoryTab({ workspaceId, reloadKey, onCardClick }) {
  const [voice, setVoice] = useState("");
  const [client, setClient] = useState("");

  const fetchRows = async ({ from, to, lanes, signal }) => {
    if (!workspaceId) return [];
    const r = await fetch(`${API}/v2/workspaces/${workspaceId}/cards`, { credentials: "include", signal });
    const all = (await r.json().catch(() => [])) || [];
    const list = Array.isArray(all) ? all : (all.items || []);
    return list.filter((c) => {
      if (!lanes.includes(c.stage)) return false;
      const ts = c.publish_date || c.scheduled_for || c.updated_at || c.created_at;
      if (!ts) return false;
      const d = String(ts).slice(0, 10);
      if (d < from || d > to) return false;
      if (voice && !(c.voice || "").toLowerCase().includes(voice.toLowerCase())) return false;
      if (client && !((c.client_label || c.client || "")).toLowerCase().includes(client.toLowerCase())) return false;
      return true;
    });
  };

  return (
    <CompletedHistoryPanel
      testidPrefix="amplify-completed"
      sparklineLabel="published-per-day"
      defaultRangeKey="30d"
      enabledRanges={["today", "this_week", "last_week", "this_month", "30d", "90d", "qtd", "ytd"]}
      lanesConfig={["COMPLETED", "PUBLISHED", "SCHEDULED", "APPROVED", "REJECTED"].map((s) => ({
        id: s, label: STAGE_LABELS[s] || s,
      }))}
      defaultLanes={["COMPLETED"]}
      lanesLabel="Stages"
      extrasKey={`${voice}|${client}|${reloadKey || ""}`}
      reloadKey={reloadKey}
      renderExtraFilters={() => (
        <>
          <div className="space-y-0.5">
            <label className="text-[10px] uppercase tracking-wide text-slate-500">Voice</label>
            <input
              type="text" value={voice}
              onChange={(e) => setVoice(e.target.value)}
              placeholder="e.g. Remi"
              className="h-8 px-2 text-xs rounded border border-slate-300 bg-white w-[140px]"
              data-testid="amplify-completed-voice"
            />
          </div>
          <div className="space-y-0.5">
            <label className="text-[10px] uppercase tracking-wide text-slate-500">Client</label>
            <input
              type="text" value={client}
              onChange={(e) => setClient(e.target.value)}
              placeholder="optional"
              className="h-8 px-2 text-xs rounded border border-slate-300 bg-white w-[140px]"
              data-testid="amplify-completed-client"
            />
          </div>
        </>
      )}
      countsBuilder={(rows) => {
        const m = { total: rows.length };
        rows.forEach((c) => { m[c.stage] = (m[c.stage] || 0) + 1; });
        return m;
      }}
      summaryRender={(counts, from, to, loading) => loading ? "Loading…" : (
        <>
          Showing <span className="font-bold">{counts.total}</span> completed item{counts.total === 1 ? "" : "s"} between <span className="font-medium">{from}</span> and <span className="font-medium">{to}</span>
          {counts.COMPLETED ? <> · {counts.COMPLETED} completed</> : null}
          {counts.PUBLISHED ? <> · {counts.PUBLISHED} published</> : null}
          {counts.SCHEDULED ? <> · {counts.SCHEDULED} scheduled</> : null}
        </>
      )}
      columns={[
        { key: "title", label: "Title",
          render: (c) => <span className="font-medium text-slate-900 truncate max-w-[260px] block">{c.title || "(untitled)"}</span>,
          csv: (c) => c.title || "(untitled)" },
        { key: "stage", label: "Stage",
          render: (c) => <Badge variant="outline" className="text-[10px]">{STAGE_LABELS[c.stage] || c.stage}</Badge>,
          csv: (c) => STAGE_LABELS[c.stage] || c.stage },
        { key: "voice", label: "Voice", className: "text-slate-600",
          render: (c) => c.voice || "—",
          csv: (c) => c.voice || "" },
        { key: "client", label: "Client", className: "text-slate-600",
          render: (c) => c.client_label || c.client || "—",
          csv: (c) => c.client_label || c.client || "" },
        { key: "date", label: "Date", className: "text-slate-500 tabular-nums text-xs",
          render: (c) => (c.publish_date || c.scheduled_for || c.updated_at || c.created_at || "").slice(0, 10),
          csv: (c) => (c.publish_date || c.scheduled_for || c.updated_at || c.created_at || "").slice(0, 10) },
      ]}
      csvHeader={["Title", "Stage", "Voice", "Client", "Date"]}
      csvFilename={(from, to) => `amplify-completed_${from}_to_${to}.csv`}
      rowId={(c) => c.card_id}
      rowDateFor={(c) => c.publish_date || c.scheduled_for || c.updated_at || c.created_at}
      onRowClick={(c) => onCardClick && onCardClick(c.card_id, c.render_mode)}
      emptyMessage="No completed items in this date range. Adjust the range or filters above."
    />
  );
}
